/**
 * Distributes mega menu promo content from <template> elements
 * to matching target containers in the desktop mega menu and mobile drawer.
 *
 * Promo blocks render server-side as <template> elements with data attributes.
 * This script clones the pre-rendered content into the correct locations
 * based on matching parent_menu + menu_item attributes.
 *
 * For desktop menu items that have a promo configured but no child links,
 * Liquid never renders a submenu wrapper (the template gates on `link.links`).
 * `ensureDesktopPromoContainers` builds a minimal submenu container so the
 * promo has somewhere to land.
 *
 * The mobile drawer has the same gap — simple `<a>` links render instead of
 * the full details/submenu panel. `ensureMobileDrawerPromoPanels` clones a
 * reference <details> element within the same gender scope to inherit
 * Horizon's SVG icons and declarative on:click attributes, then rekeys it
 * to the promo's menu_item. `distributePromos` then populates both.
 *
 * A promo block with "Show images above menu links" on carries
 * `data-image-first` on its template. `applyPromoPlacement` then moves that
 * item's promo container ahead of its link list (desktop mega menu and mobile
 * drawer), so the images come first both visually and in focus order. Blocks
 * without the flag keep the original links-then-images order.
 *
 * Uses a MutationObserver to re-distribute after Horizon's section rendering
 * API morphs the DOM (which wipes JS-injected content).
 */

const PROMO_PARENT_MENU_TO_GENDER_KEY = {
  womens: 'womens',
  mens: 'mens',
  secondary: 'world-of-hanro',
};

function ensureDesktopPromoContainers() {
  const templates = document.querySelectorAll('template[data-mega-menu-promo]');
  if (!templates.length) return;

  templates.forEach((template) => {
    const parentMenu = template.dataset.parentMenu;
    const menuItem = template.dataset.menuItem;
    if (!menuItem) return;

    const genderKey = PROMO_PARENT_MENU_TO_GENDER_KEY[parentMenu];
    const scopes = genderKey
      ? document.querySelectorAll(`[data-gender-menu="${genderKey}"]`)
      : document.querySelectorAll('header-menu');

    scopes.forEach((scope, scopeIndex) => {
      const listItems = scope.querySelectorAll('.menu-list__list-item');
      listItems.forEach((li, liIndex) => {
        if (li.hasAttribute('slot')) return;
        const link = li.querySelector('[ref="menuitem"]');
        if (!link) return;
        if (li.querySelector('[ref="submenu[]"]')) return;

        const titleEl = link.querySelector('.menu-list__link-title');
        if (!titleEl) return;
        const title = (titleEl.textContent || '').trim().toLowerCase();
        if (title !== menuItem) return;

        const idSuffix = `${genderKey || 'menu'}-${scopeIndex + 1}-${liIndex + 1}-promo`;
        const submenuId = `submenu-${idSuffix}`;
        const listId = `MegaMenuList-${idSuffix}`;

        const submenu = document.createElement('div');
        submenu.className = 'menu-list__submenu';
        submenu.setAttribute('ref', 'submenu[]');
        submenu.innerHTML = `
          <div id="${submenuId}" class="menu-list__submenu-inner">
            <div class="mega-menu section section--full-width-margin">
              <div class="mega-menu__grid" data-menu-grid-id="${listId}" data-menu-columns="6">
                <ul data-menu-list-id="${listId}" class="mega-menu__list list-unstyled" hidden></ul>
                <span class="mega-menu__content" data-promo-target data-parent-menu="${parentMenu}" data-menu-item="${menuItem}">
                  <ul class="mega-menu__content-list mega-menu__content-list--promos list-unstyled"></ul>
                </span>
              </div>
            </div>
          </div>
        `.trim();
        li.appendChild(submenu);

        link.setAttribute('aria-controls', submenuId);
        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-expanded', 'false');
      });
    });
  });
}

function ensureMobileDrawerPromoPanels() {
  const templates = document.querySelectorAll('template[data-mega-menu-promo]');
  if (!templates.length) return;

  templates.forEach((template) => {
    const parentMenu = template.dataset.parentMenu;
    const menuItem = template.dataset.menuItem;
    if (!menuItem) return;

    const genderKey = PROMO_PARENT_MENU_TO_GENDER_KEY[parentMenu];
    const drawerScopes = genderKey
      ? document.querySelectorAll(`header-drawer [data-gender-menu="${genderKey}"]`)
      : document.querySelectorAll('header-drawer');

    drawerScopes.forEach((scope) => {
      const listItems = scope.querySelectorAll('.menu-drawer__list-item');
      listItems.forEach((li) => {
        if (li.querySelector('details.menu-drawer__menu-container')) return;
        if (li.querySelector('[data-promo-target-mobile]')) return;

        const link = li.querySelector('a.menu-drawer__menu-item--mainlist');
        if (!link) return;

        const textEl = link.querySelector('.menu-drawer__menu-item-text');
        if (!textEl) return;
        const originalLabel = (textEl.textContent || '').trim();
        if (originalLabel.toLowerCase() !== menuItem) return;

        // Clone an existing details panel in the same scope to inherit Horizon's
        // markup, SVG icons, and declarative on:click event attributes (back,
        // close, open/submenu). Without a reference we can't build a faithful
        // panel from scratch.
        const refDetails = scope.querySelector('details.menu-drawer__menu-container');
        if (!refDetails) return;

        const newDetails = refDetails.cloneNode(true);

        const childlist = newDetails.querySelector('.menu-drawer__menu--childlist');
        if (childlist) childlist.innerHTML = '';

        const featuredList = newDetails.querySelector('.menu-drawer__featured-content-list');
        if (featuredList) featuredList.innerHTML = '';

        const summary = newDetails.querySelector('summary');
        summary?.classList.remove('menu-drawer__menu-item--active');
        summary?.removeAttribute('aria-expanded');
        if (summary) summary.dataset.title = originalLabel;
        const summaryText = summary?.querySelector('.menu-drawer__menu-item-text');
        if (summaryText) summaryText.textContent = originalLabel;

        const backText = newDetails.querySelector('.menu-drawer__back-button .menu-drawer__menu-item-text');
        if (backText) backText.textContent = originalLabel;

        const slug = `promo-${menuItem.replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '')}`;
        newDetails.id = `Details-menu-drawer-menu-item-${slug}`;
        if (summary) summary.id = `HeaderDrawer-${slug}`;
        const submenu = newDetails.querySelector('.menu-drawer__submenu');
        if (submenu) submenu.id = `link-${slug}`;

        const featured = newDetails.querySelector('[data-promo-target-mobile]');
        if (featured) {
          featured.dataset.parentMenu = parentMenu;
          featured.dataset.menuItem = menuItem;
          featured.hidden = true;
        }

        newDetails.removeAttribute('open');
        newDetails.classList.remove('menu-open');

        link.replaceWith(newDetails);
      });
    });
  });
}

// A column <li> can span multiple grid tracks (mega-menu__column--span-N),
// so the track total is the sum of spans, not the element count.
function countMenuTracks(menuList) {
  let tracks = 0;
  menuList.querySelectorAll('.mega-menu__column').forEach((column) => {
    const span = column.className.match(/mega-menu__column--span-(\d+)/);
    tracks += span ? parseInt(span[1], 10) : 1;
  });
  return tracks;
}

// Menus with no promos still need centering: their subgrid spans the full
// fixed grid, which leaves short menus pinned to the left edge.
function centerPlainMegaMenus() {
  document.querySelectorAll('[data-menu-grid-id]').forEach((grid) => {
    const target = grid.querySelector('[data-promo-target]');
    if (target && target.querySelector('.mega-menu__content-list-item')) return;

    const gridId = grid.dataset.menuGridId;
    const menuList = grid.querySelector(`[data-menu-list-id="${gridId}"]`);
    if (!menuList) return;

    const menuColumnCount = countMenuTracks(menuList);
    const gridColumns = parseInt(grid.dataset.menuColumns, 10) || 6;
    if (menuColumnCount === 0 || menuColumnCount >= gridColumns) return;

    // Center with symmetric fractional gutter tracks. Snapping to integer grid
    // lines (Math.floor) sent an odd leftover entirely to the left, so a menu
    // narrower than the grid sat left of center (ECSR-250). Flanking the used
    // tracks with equal fractional gutters centers any width, odd or even, while
    // each content track keeps its full-grid width.
    const gutter = (gridColumns - menuColumnCount) / 2;
    grid.style.gridTemplateColumns = `minmax(0, ${gutter}fr) repeat(${menuColumnCount}, minmax(0, 1fr)) minmax(0, ${gutter}fr)`;
    menuList.style.gridColumn = `2 / span ${menuColumnCount}`;
  });
}

function distributePromos() {
  const templates = document.querySelectorAll('template[data-mega-menu-promo]');
  if (!templates.length) return;

  templates.forEach((template) => {
    const parentMenu = template.dataset.parentMenu;
    const menuItem = template.dataset.menuItem;
    const promoCount = parseInt(template.dataset.promoCount, 10) || 0;
    const imageFirst = template.hasAttribute('data-image-first');

    if (!menuItem || promoCount === 0) return;

    // Desktop targets
    const desktopTargets = document.querySelectorAll(
      `[data-promo-target][data-parent-menu="${parentMenu}"][data-menu-item="${menuItem}"]`
    );

    desktopTargets.forEach((target) => {
      const list = target.querySelector('.mega-menu__content-list');
      if (!list || list.children.length > 0) return;

      const content = template.content.cloneNode(true);
      list.appendChild(content);

      // Position link lists and promos within the fixed grid defined by the
      // section's mega_menu_columns setting. We don't rewrite the grid's own
      // column count — just tell the list how many tracks it occupies and
      // clamp promo columns to whatever's left.
      const grid = target.closest('[data-menu-grid-id]');
      if (grid) {
        const gridId = grid.dataset.menuGridId;
        const menuList = grid.querySelector(`[data-menu-list-id="${gridId}"]`);
        const menuColumnCount = menuList ? countMenuTracks(menuList) : 1;
        const gridColumns = parseInt(grid.dataset.menuColumns, 10) || 6;
        const availableForPromos = Math.max(1, gridColumns - menuColumnCount);
        const contentCols = Math.min(promoCount, availableForPromos);

        if (menuList) {
          menuList.style.setProperty('--menu-columns-desktop', menuColumnCount);
          menuList.style.setProperty('--menu-columns-tablet', menuColumnCount);
        }
        // If there are no child links, let the promo content span the full grid.
        const effectivePromoCols = menuColumnCount === 0 ? gridColumns : contentCols;
        target.style.setProperty('--menu-content-columns-desktop', effectivePromoCols);
        target.style.setProperty('--menu-content-columns-tablet', Math.min(effectivePromoCols, gridColumns));

        // Center the used tracks and butt the promos up against the text
        // columns — the stylesheet pins the list left and anchors promos to
        // the right edge, which strands empty tracks in the middle whenever
        // fewer than the full set of columns is in play.
        const usedColumns = menuColumnCount + effectivePromoCols;
        // Strictly fewer than the full grid: a menu that already fills every
        // track needs no centering, and rewriting its template would add two
        // zero-width gutter tracks — and with them two extra column gaps, which
        // insets the content by one gap on each side (20px at --gap-xl). That
        // silently shifted the full-width Women's/Men's dropdowns inward, which
        // ECSR-250 explicitly requires to stay unchanged.
        if (menuColumnCount > 0 && usedColumns < gridColumns) {
          // Symmetric fractional gutter tracks center the text+promo block as a
          // unit. The old Math.floor integer offset biased odd totals to the
          // left, so a menu using an odd number of tracks (e.g. Stores: 1 text
          // + 2 promos = 3 on a 6-track grid) sat left of center (ECSR-250).
          // Equal fractional gutters keep each content track full width and
          // center any total, odd or even.
          const gutter = (gridColumns - usedColumns) / 2;
          grid.style.gridTemplateColumns = `minmax(0, ${gutter}fr) repeat(${usedColumns}, minmax(0, 1fr)) minmax(0, ${gutter}fr)`;
          if (imageFirst) {
            // Same centred block, promos and links swapped within it.
            target.style.gridColumn = `2 / span ${effectivePromoCols}`;
            menuList.style.gridColumn = `${2 + effectivePromoCols} / span ${menuColumnCount}`;
          } else {
            menuList.style.gridColumn = `2 / span ${menuColumnCount}`;
            target.style.gridColumn = `${2 + menuColumnCount} / span ${effectivePromoCols}`;
          }
        } else if (imageFirst && menuColumnCount > 0 && usedColumns === gridColumns) {
          // A menu that fills the grid: mirror the stylesheet's links-left,
          // promos-right placement so the promos lead on the same row.
          target.style.gridColumn = `1 / span ${effectivePromoCols}`;
          menuList.style.gridColumn = `span ${menuColumnCount} / -1`;
        }
      }
    });

    // Mobile targets
    const mobileTargets = document.querySelectorAll(
      `[data-promo-target-mobile][data-parent-menu="${parentMenu}"][data-menu-item="${menuItem}"]`
    );

    mobileTargets.forEach((target) => {
      const list = target.querySelector('.menu-drawer__featured-content-list');
      if (!list || list.children.length > 0) return;

      const content = template.content.cloneNode(true);
      content.querySelectorAll('.mega-menu__content-list-item').forEach((item) => {
        item.className = 'menu-drawer__featured-content-list-item';
      });
      list.appendChild(content);
      target.hidden = false;
    });
  });
}

/**
 * Orders each promo container relative to its link list according to the
 * block's image-first flag. Runs on every pass (not only when promos are first
 * injected) so a morph or a cloned drawer panel can never leave a container on
 * the wrong side. The first template matching a target wins, mirroring the
 * fill guard in distributePromos. Idempotent: it only moves nodes that are out
 * of place, so the observer settles after one extra pass.
 */
function applyPromoPlacement() {
  const templates = document.querySelectorAll('template[data-mega-menu-promo]');
  if (!templates.length) return;

  const placed = new Set();

  templates.forEach((template) => {
    const parentMenu = template.dataset.parentMenu;
    const menuItem = template.dataset.menuItem;
    if (!menuItem) return;
    const imageFirst = template.hasAttribute('data-image-first');

    // Desktop mega menu
    document
      .querySelectorAll(`[data-promo-target][data-parent-menu="${parentMenu}"][data-menu-item="${menuItem}"]`)
      .forEach((target) => {
        if (placed.has(target)) return;
        placed.add(target);
        if (!target.querySelector('.mega-menu__content-list-item')) return;

        const grid = target.closest('[data-menu-grid-id]');
        if (!grid) return;
        const menuList = grid.querySelector(`[data-menu-list-id="${grid.dataset.menuGridId}"]`);
        if (!menuList) return;

        // Promos lead on the same row as the links: distributePromos sizes and
        // centres the pair exactly as with the setting off, only swapped. Moving
        // the container ahead of the list here keeps focus order matching.
        const leads = imageFirst && countMenuTracks(menuList) > 0;
        const targetIsFirst = Boolean(menuList.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_PRECEDING);

        if (leads) {
          target.setAttribute('data-promo-image-first', '');
          if (!targetIsFirst) menuList.before(target);
        } else if (targetIsFirst) {
          target.removeAttribute('data-promo-image-first');
          menuList.after(target);
        }
      });

    // Mobile drawer
    document
      .querySelectorAll(`[data-promo-target-mobile][data-parent-menu="${parentMenu}"][data-menu-item="${menuItem}"]`)
      .forEach((target) => {
        if (placed.has(target)) return;
        placed.add(target);

        const childlist = target.parentElement?.querySelector(':scope > .menu-drawer__menu--childlist');
        if (!childlist) return;

        const targetIsFirst = Boolean(childlist.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_PRECEDING);

        if (imageFirst) {
          target.setAttribute('data-promo-image-first', '');
          // Animate in with the first link rather than after the last one.
          target.style.setProperty('--menu-drawer-animation-index', '1');
          if (!targetIsFirst) childlist.before(target);
        } else if (targetIsFirst) {
          // Only reachable for a drawer panel cloned from an image-first one.
          target.removeAttribute('data-promo-image-first');
          target.style.setProperty('--menu-drawer-animation-index', String(childlist.children.length + 1));
          childlist.after(target);
        }
      });
  });
}

function init() {
  ensureDesktopPromoContainers();
  ensureMobileDrawerPromoPanels();
  distributePromos();
  applyPromoPlacement();
  centerPlainMegaMenus();

  // Watch for Horizon's section rendering API DOM morphs.
  // When Horizon re-renders the header section, it replaces mega menu HTML,
  // wiping our injected promos and containers. The observer re-applies both
  // after each morph. All of these functions are idempotent — they guard by
  // checking for existing content or position before changing anything.
  const header = document.querySelector('.header-section');
  if (header) {
    new MutationObserver(() => {
      ensureDesktopPromoContainers();
      ensureMobileDrawerPromoPanels();
      distributePromos();
      applyPromoPlacement();
      centerPlainMegaMenus();
    }).observe(header, { childList: true, subtree: true });
  }
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
